import { Form } from "@inertiajs/react"
import { useState } from "react"
import { useTranslation } from "react-i18next"

import { VariantStep } from "@/components/missions/variant-step"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Label } from "@/components/ui/label"
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group"
import { formatGiftValue } from "@/lib/format-gift-value"
import { cn } from "@/lib/utils"
import {
  creatorCollabProductChoicePath,
  creatorCollabTransitionsPath,
} from "@/routes"
import type { Locale } from "@/types"
import type { CollabOfferedProduct } from "@/types/collab"

/**
 * OKS-990 — the drop picker (UX Spec §S3).
 *
 * A drop may offer several products. The creator — who knows their shade, size
 * and taste — chooses which one lands in the parcel, and the variant within it
 * when there is a real choice. The pick is recorded at grant and is what the
 * brand packs.
 *
 * Inertia `<Form>` + plain `Label` / `RadioGroup` (Crew rule). No
 * react-hook-form, no zod.
 *
 * # ⚠️ ONE dialog, TWO steps — never two dialogs
 *
 * Step 1 → step 2 is an in-place content swap so *Back* costs no navigation
 * and preserves the step-1 selection (UX §4). The dialog is mounted OUTSIDE
 * the sticky bar and outside the gift section, exactly where
 * `RequestAnotherDialog` is mounted and for the same reason: a re-render of
 * the gift section must not drop an in-progress selection.
 *
 * # ⚠️ Selecting a product does NOT auto-advance (UX-A2)
 *
 * An auto-advance on a radio makes *Back* the only way to fix a mis-tap, on
 * the surface where mis-taps are most likely — 44px targets, single column,
 * thumb reach. The creator confirms.
 *
 * # ⚠️ An exhausted product is shown, not filtered out (UX-A4)
 *
 * A silently shorter list reads as a smaller offer; a badged one reads as an
 * honest offer, and it is what makes "a run on one shade never blocks
 * participation" legible. It is `aria-disabled` and keeps its cause IN ITS
 * ACCESSIBLE NAME — a screen-reader user must never meet a nameless disabled
 * control.
 *
 * # ⚠️ The server is the gate; this is the courtesy
 *
 * `Missions::CapacityGuard` refuses a request for an exhausted product inside
 * the serialised transaction. Everything here is what keeps the ordinary case
 * from making a round trip it does not need. A product that sells out between
 * render and submit comes back as an Inertia FIELD error on
 * `mission_product_id` — the dialog stays open, the selection is preserved,
 * and the creator picks another one where they already are.
 *
 * # ⚠️ Base UI radio hazard (UX §S3)
 *
 * A generated `id` on a Base UI radio lands on the HIDDEN `<input>`, not on
 * the exposed `role="radio"` span — and `<input type="radio">` carries an
 * implicit radio role, so a `toHaveRole("radio")` assertion keyed on your own
 * id passes HOLLOWLY. Query an option through its card
 * (`within(card).getByRole("radio")`), never through a hand-assigned id.
 *
 * # OKS-1040 — `mode: "post_grant"` (UX §2.1, UQ1)
 *
 * The SAME offer and the same steps, for a creator who is already granted and
 * chooses afterwards (or changes a pick the brand made for them). Three
 * differences, nothing else:
 *
 *   - POSTs to `creatorCollabProductChoicePath` (`Collabs::ChooseProduct`),
 *     with no `event` / `no_ship` fields;
 *   - an exhausted product stays SELECTABLE, with the « il arrivera plus
 *     tard » helper — capacity never refuses a post-grant pick (R13 posture);
 *   - the submit reads « Choisir ce produit », and a dialog-level refusal
 *     (`offer_changed` / `not_shippable`) renders as an Alert at the top.
 *
 * `request` mode — the default — is byte-identical to OKS-990.
 */
export function DropPickerDialog({
  open,
  onOpenChange,
  collabId,
  missionTitle,
  brandName,
  offer,
  locale,
  serverErrors,
  rerequestReason,
  mode = "request",
  initialProductId,
  initialVariantId,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  collabId: number
  missionTitle: string
  brandName: string
  /** The offered set, `(position, id)` order — the brand's authoring order. */
  offer: CollabOfferedProduct[]
  /**
   * OKS-1000 — threaded rather than read from i18next, so the picker formats
   * money through the SAME locale the offer list above it was handed. Two
   * surfaces on one page deriving the locale two ways is how they come to
   * disagree about one product's price.
   */
  locale: Locale
  /** Inertia field errors, keyed by the param they belong to. */
  serverErrors?: {
    mission_product_id?: string
    product_variant_id?: string
  }
  /**
   * OKS-990 F-MOUNT route 2 — set only when the picker was reached THROUGH
   * `RequestAnotherDialog` (a TTL-flagged creator re-requesting). Posted as a
   * hidden field so the reason and the pick arrive in one transaction.
   */
  rerequestReason?: string
  /** OKS-1040 — see the class doc. */
  mode?: "request" | "post_grant"
  /** OKS-1040 — « Modifier » opens pre-selected on the current pick (UX §4). */
  initialProductId?: number | null
  initialVariantId?: number | null
}) {
  const { t } = useTranslation()
  const postGrant = mode === "post_grant"
  const initialProduct = initialProductId ? String(initialProductId) : ""
  const initialVariant = initialVariantId ? String(initialVariantId) : ""
  const [productId, setProductId] = useState<string>(initialProduct)
  const [variantId, setVariantId] = useState<string>(initialVariant)
  const [step, setStep] = useState<"product" | "variant">("product")
  const [clientError, setClientError] = useState<string | null>(null)

  const chosen =
    offer.find((product) => String(product.id) === productId) ?? null
  // The variant step renders only when the chosen product has a REAL choice.
  // `variant_count` is the CHOOSABLE count — the archived filter already
  // applied server-side (PRD R5) — so a product whose second variant was
  // delisted correctly skips this step.
  const needsVariantStep = (chosen?.variant_count ?? 0) >= 2

  const error =
    clientError ??
    serverErrors?.mission_product_id ??
    serverErrors?.product_variant_id ??
    null
  const errorId = "drop-picker-error"
  function reset() {
    setProductId(initialProduct)
    setVariantId(initialVariant)
    setStep("product")
    setClientError(null)
  }

  // ⚠️ The wizard's own state is NOT driven by the request lifecycle.
  //
  // The first version advanced the step inside Inertia's `onBefore` — returning
  // false to cancel the POST. It worked in a browser and was wrong in kind:
  // "move to the next step" is UI state, and routing it through the pre-request
  // hook means the step only advances if a request was attempted. Writing the
  // arms surfaced it (nothing advanced under test, because no request is made),
  // but the coupling was the defect, not the test.
  //
  // So: the footer button SUBMITS only on the final step and is a plain button
  // before that. `onBefore` keeps only the job it should have — the last
  // client-side check before the POST leaves.
  // ⚠️ `chosen != null` is the load-bearing clause. Without it, a click with
  // NOTHING selected made the button a submit — because `needsVariantStep` is
  // derived from the chosen product, and with no choice it reads false, which
  // looks like "no variant step, so this is the last one". The dialog then
  // POSTed an empty pick for the server to reject instead of saying so on the
  // spot. Submitting is never right with no selection.
  const isFinalStep =
    chosen != null && (step === "variant" || !needsVariantStep)

  // ⚠️ OKS-1040 (PO 3.6, ≥90) — `preventDefault` is LOAD-BEARING. This
  // handler moves to the variant step, which re-renders THIS SAME button as
  // `type="submit"` before the browser runs the click's default action — so
  // without it the form submits an empty variant on « Continuer ». Not
  // observable in jsdom (its activation reads the type too early); the arm
  // that can red is the Playwright one.
  function advance(event: { preventDefault: () => void }) {
    event.preventDefault()
    if (!chosen) {
      setClientError(t("collabs.transitions.errors.product_required"))
      return
    }
    setClientError(null)
    setStep("variant")
  }

  // The ordinary case never leaves the page; the server re-checks regardless
  // (`Collabs::RequestProduct` resolves the pick inside its transaction).
  function validate(): boolean {
    if (!chosen) {
      setClientError(t("collabs.transitions.errors.product_required"))
      return false
    }
    if (needsVariantStep && !variantId) {
      setClientError(t("collabs.transitions.errors.variant_required"))
      return false
    }
    setClientError(null)
    return true
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) reset()
        onOpenChange(next)
      }}
    >
      <DialogContent
        data-testid="drop-picker-dialog"
        aria-label={t("collabs.drop_picker.aria.dialog", {
          title: missionTitle,
        })}
      >
        <DialogHeader>
          <DialogTitle>
            {step === "product"
              ? t("collabs.drop_picker.product_step.title")
              : t("collabs.drop_picker.variant_step.title")}
          </DialogTitle>
          <DialogDescription>
            {step === "product"
              ? t("collabs.drop_picker.product_step.body", { brand: brandName })
              : t("collabs.drop_picker.variant_step.body", {
                  product: chosen?.name ?? "",
                })}
          </DialogDescription>
        </DialogHeader>

        <Form
          action={
            postGrant
              ? creatorCollabProductChoicePath(collabId)
              : creatorCollabTransitionsPath(collabId)
          }
          method="post"
          onBefore={validate}
          /* OKS-1040 — post-grant only: a successful « Modifier » keeps the
             product block mounted, so the dialog must close itself. A first
             pick unmounts the Choose block (and this dialog) anyway. The
             request-mode note below is unchanged. */
          onSuccess={postGrant ? () => onOpenChange(false) : undefined}
          /* ⚠️ NO `onSuccess` HERE, DELIBERATELY, and it was written and then
             removed rather than never considered.
             UX §S3's success state is "dialog closes, flash on the landed
             surface". `MissionDetail` gets that by deriving `open` from
             `lifecycle.state === "assigned"`, which covers EVERY route to the
             new props — the grant, a partial reload, a Back — not just the one
             a callback sees. With that in place a success callback could only
             reset this dialog's own step state, and after a grant the collab
             has left `assigned`, so the dialog can never re-open on this page
             to show it. The reset was unreachable by construction, and an
             unreachable branch that no arm can exercise is indistinguishable
             from a broken one. Drill M3 confirmed it: removing the callback
             reds nothing, while removing the derivation reds two arms. */
        >
          {({ processing, errors: formErrors }) => {
            // OKS-1040 — post-grant errors come from THIS form's own state,
            // never the page error bag (`docs/frontend/inertia-error-bag.md`:
            // a manual `only:` reload wipes it and Back replays it). Request
            // mode keeps its OKS-990 `serverErrors` wiring unchanged.
            const shownError = postGrant
              ? (clientError ??
                formErrors.mission_product_id ??
                formErrors.product_variant_id ??
                null)
              : error
            const baseError = postGrant ? (formErrors.base ?? null) : null

            return (
              <>
                {baseError && (
                  <Alert
                    variant="destructive"
                    role="alert"
                    data-testid="drop-picker-base-error"
                  >
                    <AlertDescription>{baseError}</AlertDescription>
                  </Alert>
                )}
                {!postGrant && (
                  <input type="hidden" name="event" value="apply" />
                )}
                {/* ⚠️ OKS-990 F-MOUNT route 2 — the re-request reason travels
                  WITH the pick. A TTL-flagged creator on a multi-product drop
                  reaches the picker through `RequestAnotherDialog`, which hands
                  the reason over rather than posting it; without this field the
                  reason is silently dropped and the server answers
                  `reason_required` on a dialog that has already closed. Absent
                  entirely on the normal path, where the server neither expects
                  nor reads it. */}
                {rerequestReason && (
                  <input
                    type="hidden"
                    name="rerequest_reason"
                    value={rerequestReason}
                    data-testid="drop-picker-rerequest-reason"
                  />
                )}
                {/* The creator is asking for a shipment. Stated explicitly so the
                  server never infers it from the pick's presence. */}
                {!postGrant && (
                  <input type="hidden" name="no_ship" value="false" />
                )}
                <input
                  type="hidden"
                  name="mission_product_id"
                  value={productId}
                />
                <input
                  type="hidden"
                  name="product_variant_id"
                  value={variantId}
                />

                {/* ⚠️ The list scrolls INSIDE the dialog body and the footer stays
                  pinned, so the submit is reachable without scrolling past N
                  products (UX §5). Single column at every width — a 402px 2-up
                  puts a 32-character product name in ~170px. */}
                <div
                  className="max-h-[50vh] min-w-0 overflow-y-auto"
                  data-testid="drop-picker-step"
                  data-step={step}
                >
                  {step === "product" ? (
                    <ProductStep
                      locale={locale}
                      offer={offer}
                      postGrant={postGrant}
                      value={productId}
                      onChange={(next) => {
                        setProductId(next)
                        setVariantId("")
                        setClientError(null)
                      }}
                      errorId={shownError ? errorId : undefined}
                    />
                  ) : (
                    <VariantStep
                      product={chosen}
                      value={variantId}
                      onChange={(next) => {
                        setVariantId(next)
                        setClientError(null)
                      }}
                      errorId={shownError ? errorId : undefined}
                    />
                  )}
                </div>

                {shownError && (
                  <p
                    id={errorId}
                    data-testid="drop-picker-error"
                    aria-live="polite"
                    className="text-destructive text-sm"
                  >
                    {shownError}
                  </p>
                )}

                <DialogFooter>
                  {step === "variant" ? (
                    <Button
                      type="button"
                      variant="ghost"
                      onClick={() => {
                        setStep("product")
                        setClientError(null)
                      }}
                      data-testid="drop-picker-back"
                    >
                      {t("collabs.drop_picker.back_to_products")}
                    </Button>
                  ) : (
                    <Button
                      type="button"
                      variant="ghost"
                      onClick={() => onOpenChange(false)}
                    >
                      {t("collabs.drop_picker.cancel")}
                    </Button>
                  )}
                  <Button
                    type={isFinalStep ? "submit" : "button"}
                    onClick={isFinalStep ? undefined : advance}
                    disabled={processing}
                    data-testid="drop-picker-submit"
                  >
                    {postGrant
                      ? processing
                        ? t("collabs.product_choice.submitting")
                        : t("collabs.product_choice.submit")
                      : processing
                        ? t("collabs.drop_picker.submitting")
                        : t("collabs.drop_picker.submit")}
                  </Button>
                </DialogFooter>
              </>
            )
          }}
        </Form>
      </DialogContent>
    </Dialog>
  )
}

function ProductStep({
  offer,
  locale,
  value,
  onChange,
  errorId,
  postGrant = false,
}: {
  offer: CollabOfferedProduct[]
  locale: Locale
  value: string
  onChange: (next: string) => void
  errorId?: string
  /** OKS-1040 — exhausted stays selectable, with the « later » helper. */
  postGrant?: boolean
}) {
  const { t } = useTranslation()

  return (
    <RadioGroup
      value={value}
      onValueChange={onChange}
      aria-label={t("collabs.drop_picker.product_step.title")}
      aria-describedby={errorId}
      className="flex flex-col gap-2"
    >
      {offer.map((product) => {
        const id = `drop-picker-product-${product.id}`
        // ⚠️ The cause travels IN the accessible name, not only in the badge —
        // a screen-reader user must never meet a nameless disabled control.
        const label = product.exhausted
          ? `${product.name} — ${t("collabs.drop_picker.exhausted")}`
          : product.name
        // OKS-1040 — post-grant: never disabled (R13 posture).
        const blocked = product.exhausted && !postGrant

        return (
          <div
            key={product.id}
            data-testid="drop-picker-product-option"
            data-exhausted={product.exhausted ? "true" : "false"}
            className={cn(
              "flex min-w-0 items-start gap-3 rounded-md border p-3",
              blocked && "opacity-60",
            )}
          >
            <RadioGroupItem
              value={String(product.id)}
              id={id}
              disabled={blocked}
              aria-disabled={blocked || undefined}
              aria-label={label}
              className="mt-1 shrink-0"
            />
            <Label
              htmlFor={id}
              className="min-w-0 flex-1 cursor-pointer space-y-1 font-normal"
            >
              {/* Name WRAPS rather than truncates — it is the identifying
                  information (UX §5). */}
              <span className="block wrap-anywhere">{product.name}</span>
              <span className="text-muted-foreground block text-xs">
                {t("collabs.drop_picker.quantity", { count: product.quantity })}
                {product.variant_count >= 2 &&
                  ` · ${t("collabs.detail_offer.variants", { count: product.variant_count })}`}
              </span>
              {/* OKS-1000 (D7 S5) — the SAME token, strings and formatter the
                  read-only offer list uses. This is the screen where the
                  creator actually chooses, so the list stating a value and the
                  dialog below it omitting one is the inconsistency they meet
                  first. */}
              <PickerValue product={product} locale={locale} />
              {product.exhausted && (
                <>
                  <Badge variant="destructive">
                    {t("collabs.drop_picker.exhausted")}
                  </Badge>
                  <span className="text-muted-foreground block text-xs">
                    {postGrant
                      ? t("collabs.no_ship.dialog.pick_later_helper")
                      : t("collabs.drop_picker.exhausted_helper")}
                  </span>
                </>
              )}
            </Label>
          </div>
        )
      })}
    </RadioGroup>
  )
}

/**
 * OKS-1000 (D7 S5) — the offered row's value, identical to `OfferList`'s.
 *
 * ⚠️ Same strings, same formatter, same `none` suppression as the read-only
 * offer list on the page behind this dialog. They describe the same products,
 * so a second implementation here is how one product comes to be worth two
 * different amounts on one screen.
 */
function PickerValue({
  product,
  locale,
}: {
  product: CollabOfferedProduct
  locale: Locale
}) {
  const { t } = useTranslation()
  if (product.value_source === "none") return null

  const value = formatGiftValue(
    product.effective_value_cents,
    product.effective_currency,
    locale,
  )
  if (value == null) return null

  return (
    <span
      className="text-primary block text-xs font-semibold"
      data-testid="drop-picker-product-value"
      data-value-source={product.value_source}
    >
      {product.value_is_range
        ? t("collabs.detail_offer.value_from", { value })
        : value}
    </span>
  )
}
